import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { portalApi } from '../../api/portal';
import { tasksApi } from '../../api/tasks';
import { companiesApi } from '../../api/companies';
import { Ticket, BookOpen, Clock, ArrowRight, ClipboardCheck } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { StatusBadge } from '../../components/badges';
import TaskDetailModal from '../../components/tickets/TaskDetailModal';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { PORTAL_TILES } from '../../lib/nav';
import type { MyTask } from '../../types';

export default function PortalDashboard() {
  const { user } = useAuth();
  const { currentCompany } = useCompany();
  const myId: string | null = (user as any)?.id ?? (user as any)?._id ?? null;
  const companyId: string | undefined = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const [openTask, setOpenTask] = useState<MyTask | null>(null);
  // An outside collaborator holds no membership here: they work the tickets
  // they were attached to and nothing else, so "new ticket" and "join a
  // company" would both just fail for them.
  const isGuest = !!(currentCompany as any)?.isGuest;
  const portalTiles = isGuest
    ? PORTAL_TILES.filter(t => t.to === '/portal/tickets' || t.to === '/portal/kb')
    : PORTAL_TILES;

  const { data, isLoading } = useQuery({
    queryKey: ['portal-dashboard'],
    queryFn: () => portalApi.getDashboard(),
  });

  // Sub-ticket tasks assigned to you — including on tickets you cannot open
  // (a task assignee is not automatically on the parent). Each opens in the
  // task dialog, which the server scopes to what you may see.
  const { data: myTasks } = useQuery({
    queryKey: ['tasks', 'mine-portal'],
    queryFn: () => tasksApi.mine('open'),
    enabled: !!currentCompany,
  });
  const { data: members = [] } = useQuery({
    queryKey: ['company-members', companyId],
    queryFn: () => companiesApi.getMembers(companyId!),
    enabled: !!companyId && !!openTask,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 pb-10">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-row-odd rounded w-64" />
          <div className="h-32 bg-row-odd rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title={`Welcome${data?.companyName ? ` to ${data.companyName}` : ''}`}
        intro={isGuest
          ? 'You have been given access to specific tickets here. They are listed below.'
          : (data?.welcomeMessage || 'Open a ticket, track your requests, and find answers in the knowledge base.')}
        actions={isGuest ? undefined : (
          <Link to="/portal/tickets/new" className="btn btn-primary">
            New Ticket
          </Link>
        )}
      />

      {/* Open tickets stat */}
      <div className="card">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl brand-gradient flex items-center justify-center">
            <Ticket className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-2xl font-bold text-fg">{data?.openTickets ?? 0}</p>
            <p className="text-sm text-muted">Open Tickets</p>
          </div>
          <Link to="/portal/tickets" className="ml-auto text-sm text-primary hover:underline flex items-center gap-1">
            View all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* Tasks for you */}
      {myTasks && myTasks.tasks.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-line">
            <h2 className="section-title !mb-0 flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4 text-primary" /> Tasks for you
              <span className="text-xs text-muted font-normal">{myTasks.tasks.length} open</span>
            </h2>
          </div>
          <ul>
            {myTasks.tasks.map(task => (
              <li key={task.id} className="border-b border-line-light last:border-0">
                <button
                  type="button"
                  onClick={() => setOpenTask(task)}
                  className="w-full text-left flex items-center gap-4 px-6 py-3 hover:bg-row-odd transition-colors"
                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-fg truncate">{task.title}</p>
                    <p className="text-xs text-muted truncate">
                      <span className="font-mono font-bold text-primary mr-1.5">#{task.ticket.ticketNumber}</span>
                      {task.ticket.title}
                    </p>
                  </div>
                  <span className="text-[11px] font-bold shrink-0 rounded-full px-2 py-0.5" style={{ color: 'var(--scpa-primary)', background: 'var(--scpa-primary-muted)' }}>
                    Yours
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {openTask && (
        <TaskDetailModal
          ticketId={openTask.ticketId}
          taskId={openTask.id}
          linkBase="/portal/tickets"
          members={members as any[]}
          myId={myId}
          onClose={() => setOpenTask(null)}
        />
      )}

      {/* Destination tiles */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {portalTiles.map((tile) => {
          const Icon = tile.icon;
          return (
            <Link key={tile.to} to={tile.to} className="card hover:border-primary transition-colors">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-muted text-primary flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-fg">{tile.label}</p>
                  <p className="text-sm text-muted leading-relaxed">{tile.description}</p>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Recent tickets */}
      {data?.recentTickets && data.recentTickets.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-line">
            <h2 className="section-title !mb-0">Recent Tickets</h2>
            <Link to="/portal/tickets" className="text-sm text-primary hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div>
            {data.recentTickets.map((t) => (
              <Link
                key={t.id}
                to={`/portal/tickets/${t.id}`}
                className="flex items-center gap-4 px-6 py-3 hover:bg-row-odd transition-colors border-b border-line-light last:border-0"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-fg truncate">
                    <span className="font-mono font-bold text-primary mr-2">#{t.ticketNumber}</span>
                    {t.title}
                  </p>
                  <p className="text-xs text-muted flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3" />
                    {new Date(t.updatedAt).toLocaleDateString()}
                  </p>
                </div>
                <StatusBadge status={t.status} />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Recent KB articles */}
      {data?.recentArticles && data.recentArticles.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-line">
            <h2 className="section-title !mb-0">Knowledge Base</h2>
            <Link to="/portal/kb" className="text-sm text-primary hover:underline flex items-center gap-1">
              Browse all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div>
            {data.recentArticles.map((a) => (
              <Link
                key={a.id}
                to={`/portal/kb/${a.slug}`}
                className="flex items-center gap-4 px-6 py-3 hover:bg-row-odd transition-colors border-b border-line-light last:border-0"
              >
                <BookOpen className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-fg truncate">{a.title}</p>
                  {a.excerpt && <p className="text-xs text-muted truncate">{a.excerpt}</p>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
